import React, { useState } from "react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Edit, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Agent } from "@/models/agent";
import { formatDistanceToNow } from "date-fns";

interface AgentTableProps {
  agents: Agent[];
  onEdit?: (agent: Agent) => void;
  onDelete?: (agent: Agent) => void;
}

const AgentTable: React.FC<AgentTableProps> = ({ 
  agents, 
  onEdit, 
  onDelete 
}) => {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Agent | null;
    direction: 'ascending' | 'descending';
  }>({ key: 'createdAt', direction: 'descending' });

  const sortedAgents = React.useMemo(() => {
    let sortableAgents = [...agents];
    if (sortConfig.key) {
      sortableAgents.sort((a, b) => {
        if (a[sortConfig.key!] < b[sortConfig.key!]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key!] > b[sortConfig.key!]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableAgents;
  }, [agents, sortConfig]);

  const requestSort = (key: keyof Agent) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (
      sortConfig.key === key &&
      sortConfig.direction === 'ascending'
    ) {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  return (
    <div className="w-full overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[200px]">Name</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Pricing</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Downloads</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedAgents.map((agent) => (
            <TableRow key={agent.id}>
              <TableCell className="font-medium">{agent.name}</TableCell>
              <TableCell>{agent.category}</TableCell>
              <TableCell>
                <PricingStatus pricingModel={agent.pricingModel} />
              </TableCell>
              <TableCell>
                {agent.isPublic ? (
                  <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                    Public
                  </Badge>
                ) : (
                  <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-200">
                    Private
                  </Badge>
                )}
              </TableCell>
              <TableCell>{agent.downloads}</TableCell>
              <TableCell>{formatDistanceToNow(new Date(agent.createdAt), { addSuffix: true })}</TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigate(`/agent/${agent.id}`)}
                    title="View Agent"
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  {onEdit && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => onEdit(agent)}
                      title="Edit Agent"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      variant="outline"
                      size="icon"
                      className="text-destructive"
                      onClick={() => onDelete(agent)}
                      title="Delete Agent"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
          {agents.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                No agents found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

const PricingStatus: React.FC<{ pricingModel: string }> = ({ pricingModel }) => {
  return (
    <div className="flex items-center">
      <span
        className={`px-2 py-1 rounded-full text-xs ${
          pricingModel === "free"
            ? "bg-green-100 text-green-800"
            : "bg-blue-100 text-blue-800"
        }`}
      >
        {pricingModel === "free" ? "Free" : "Paid"}
      </span>
    </div>
  );
};

export default AgentTable;
